import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useProjectAuth } from "hooks/project/useProjectAuth";
import { readCachedProjectDetail } from "hooks/useProjects/projectDetailQuery";
import { transformProjectData } from "hooks/useProjects/projectTransform";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import { Permissions } from "utils/permissions/permissionConstants";

import { isWriterOtpLicensedProject } from "./writerStudioUpgrade";

type WriterStudioUpgradeProject = Pick<ProjectState, "name" | "otpSeating" | "isLicensed">;

export function useWriterStudioUpgrade(
  projectIdOverride?: string,
  projectOverride?: WriterStudioUpgradeProject | null
) {
  const queryClient = useQueryClient();
  const { projectId: paramProjectId } = useParams<{ projectId: string }>();
  const projectId = projectIdOverride ?? paramProjectId ?? "";
  const {
    canProject,
    isLoading: authLoading,
    embeddedProjectLicense,
  } = useProjectAuth(projectId || undefined);
  const canScheduleRerun = canProject(Permissions.PROJECT_SCHEDULE_RERUN);

  const project = useMemo((): WriterStudioUpgradeProject | null => {
    if (projectOverride) {
      return projectOverride;
    }
    if (!projectId) {
      return null;
    }
    const payload = readCachedProjectDetail(queryClient, projectId);
    if (!payload) {
      return null;
    }
    return transformProjectData(payload);
  }, [projectId, projectOverride, queryClient]);

  const [writerStudioUpgradeModalOpen, setWriterStudioUpgradeModalOpen] = useState(false);

  const needsStudioUpgrade = useMemo(() => {
    if (authLoading || !projectId) {
      return false;
    }
    if (embeddedProjectLicense.isLicensed !== true) {
      return false;
    }
    if (isWriterOtpLicensedProject(project)) {
      return true;
    }
    return project?.otpSeating?.licenseTier === "writer" && !canScheduleRerun;
  }, [authLoading, projectId, embeddedProjectLicense.isLicensed, project, canScheduleRerun]);

  const openWriterStudioUpgradeModal = useCallback(() => {
    setWriterStudioUpgradeModalOpen(true);
  }, []);

  const closeWriterStudioUpgradeModal = useCallback(() => {
    setWriterStudioUpgradeModalOpen(false);
  }, []);

  return {
    needsStudioUpgrade,
    writerStudioUpgradeModalOpen,
    openWriterStudioUpgradeModal,
    closeWriterStudioUpgradeModal,
    projectId,
    projectName: project?.name,
  };
}
